import { useCallback } from "react"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import { IPermissionCode, usePermission } from "../permission"
import { IAuth } from "../../store/Auth"

/**
 * Delivery management: the ONE place that says which action exists in which status and which
 * permission it needs (canvas Ord_Actions + Ord_DeliveryModals). Codes are the backend's
 * (docs/plans/2026-10-20-order-admin-phase2-be-contract.md §1); several codes mean "any of".
 * Every action button on the hub tabs, the approve page and the handover screens reads it:
 * an action that applies but is not allowed stays visible, aria-disabled, with the reason.
 */
export type DeliveryAction =
  // Delivery note (DN)
  | "detail"
  | "addToRoute"
  | "createRoute"
  | "notifyBuyer"
  | "consolidate"
  | "approveLoading"
  | "trackLink"
  | "callReceiver"
  | "reportFailed"
  | "manualComplete"
  | "cancel"
  | "regeneratePdf"
  | "dnReassign"
  | "addNote"
  | "podVerify"
  | "podReject"
  // Failed delivery exception
  | "reassign"
  | "confirmAttribution"
  | "escalate"
  // Handover (custody transfer)
  | "handoverCreate"
  | "handoverDetail"
  | "handoverInitiate"
  | "senderVerify"
  | "receiverVerify"
  // COD reconciliation
  | "codSettle"
  | "codDispute"
  | "codWriteOff"
  // Pull sheet (ачилтын хуудас) and its shortages
  | "pullSheetDetail"
  | "pullSheetPdf"
  | "pullSheetPdfRegenerate"
  | "shortageView"
  | "shortageReport"
  // Delivery route (маршрут)
  | "routeStart"
  | "routeLoadingComplete"
  | "routeComplete"
  | "routeReorder"
  | "routeOptimize"
  | "routeAssign"
  // Vehicle (тээврийн хэрэгсэл)
  | "vehicleCreate"
  | "vehicleEdit"
  | "vehicleDelete"

export const DELIVERY_ACTION_PERMISSION: Record<DeliveryAction, IPermissionCode[]> = {
  detail: ["ORD_DN_DETAIL"], // delivery-note/get.ts
  addToRoute: ["DEL_ROUTE_PLAN"], // delivery-route/add-stops.ts
  createRoute: ["DEL_ROUTE_PLAN"], // delivery-route/create.ts
  notifyBuyer: ["ORD_DN_MNG"], // delivery-management/progress-note.ts
  consolidate: ["ORD_DN_CREATE"], // consolidation bulk create (delivery-note/create)
  approveLoading: ["ORD_DN_PULLSHEET_ASSIGN"], // delivery-note/approve.ts
  trackLink: ["ORD_DN_MNG"], // delivery-note/public-track-link.ts
  callReceiver: [], // tel: link, no backend call
  reportFailed: ["ORD_DN_MNG"], // delivery-note/failed-report.ts
  manualComplete: ["ORD_DN_MNG"], // delivery-management/manual-complete.ts
  cancel: ["ORD_DN_MNG"], // delivery-management/cancel.ts
  regeneratePdf: ["ORD_DN_PDF"], // delivery-management/regenerate-pdf.ts
  dnReassign: ["DEL_CHANNEL_OVERRIDE"], // delivery-note/failed-reassign.ts (DN detail «Солих / Оноох»)
  addNote: ["ORD_DN_MNG"], // delivery-management/note.ts
  podVerify: ["ORD_DN_MNG"], // delivery-note/pod-verify
  podReject: ["ORD_DN_MNG"], // delivery-note/pod-reject
  reassign: ["DEL_CHANNEL_OVERRIDE"], // delivery-note/failed-reassign.ts
  confirmAttribution: ["DEL_CHANNEL_OVERRIDE"], // failed-confirm-attribution.ts
  escalate: ["DEL_CHANNEL_OVERRIDE"], // exception-escalate.ts
  handoverCreate: ["ORD_DN_MNG"], // custody/transfer-create.ts
  handoverDetail: ["ORD_DN_MNG"], // custody/transfer-get.ts
  handoverInitiate: ["ORD_DN_MNG"], // custody/transfer-initiate.ts
  senderVerify: ["ORD_DN_MNG"], // custody/transfer-sender-verify.ts (+ caller = fromUser)
  receiverVerify: ["ORD_DN_MNG"], // custody/transfer-receiver-verify.ts (+ caller = toUser)
  codSettle: ["BIZ_GOV_MNG"], // cod-reconciliation/settle.ts decision=SETTLED
  codDispute: ["BIZ_GOV_MNG"], // decision=DISPUTED
  codWriteOff: ["BIZ_GOV_MNG"], // decision=WRITTEN_OFF
  pullSheetDetail: ["ORD_PS_DETAIL"], // pull-sheet/get.ts
  pullSheetPdf: ["ORD_PS_PDF"], // pull-sheet/pdf.ts
  pullSheetPdfRegenerate: ["ORD_PS_PDF"], // pull-sheet/pdf.ts?regenerate=1
  shortageView: ["ORD_PS_DETAIL"], // pull-sheet/shortage-list.ts, shortage-decision.ts
  shortageReport: ["ORD_DN_PULLSHEET_ASSIGN"], // pull-sheet/line-shortage-report.ts
  routeStart: ["DEL_ROUTE_PLAN", "ORD_PS_MNG"], // delivery-route/start.ts
  routeLoadingComplete: ["ORD_PS_MNG", "DEL_ROUTE_PLAN"], // delivery-route/loading-complete.ts
  routeComplete: ["DEL_ROUTE_PLAN"], // delivery-route/complete.ts
  routeReorder: ["DEL_ROUTE_PLAN"], // delivery-route/reorder-stops.ts
  routeOptimize: ["DEL_ROUTE_PLAN"], // delivery-route/optimize.ts
  routeAssign: ["DEL_ROUTE_PLAN"], // delivery-route/update-assignment.ts
  vehicleCreate: ["BIZ_GOV_MNG"], // vehicle/create.ts
  vehicleEdit: ["BIZ_GOV_MNG"], // vehicle/update.ts
  vehicleDelete: ["BIZ_GOV_MNG"], // vehicle/remove.ts
}

/** Delivery note statuses the list endpoints return as "active" (contract §2 active-stats). */
export const ACTIVE_DN_STATUSES = ["ASSIGNED", "LOADED", "PENDING_HANDOVER", "DELIVERING"] as const

/** Anything an action is evaluated against. Fields are optional so one type covers DN, exception, transfer, COD. */
export type DeliveryActionSubject = {
  deliveryNoteStatus?: string | null
  deliveryChannel?: string | null
  status?: string | null
  hasReceipt?: boolean | null
  /** GET /delivery_note/:id status preconditions (contract §2); `reason` is an errors-namespace key. */
  allowedActions?: Partial<Record<"manualComplete" | "cancel" | "regeneratePdf", { allowed: boolean; reason: string | null }>> | null
  receiverPhone?: string | null
  fromUserId?: string | null
  toUserId?: string | null
  /** Pull sheet: status and the date the driver took the goods (after it no shortage can be reported). */
  pullSheetStatus?: string | null
  dispatchedDate?: string | null
  /** Route: its stops with the DN summary, to see whether the goods are loaded before start. */
  stops?: Array<{ dn?: { deliveryNoteStatus?: string | null } | null }> | null
}

type Ctx = { userId: string | null }

const ROUTABLE = ["DRAFT", "ASSIGNED"]
const LOCAL_CHANNEL = (ch?: string | null) => ch == null || ch === "LOCAL_DELIVERY"
const OPEN_EXCEPTION = ["OPEN", "IN_PROGRESS", "ESCALATED"]
/** Route statuses before it leaves: start, loading-complete, assignment, reorder, optimize (BE allows PLANNED | LOADING). */
const ROUTE_PLANNING = ["PLANNED", "LOADING"]
const DN_TERMINAL = ["DELIVERED", "CANCELLED", "RETURNED"]
/** DN statuses whose goods are not on the truck yet; BE delivery-route/start.ts refuses while any is left. */
const DN_NOT_LOADED = ["DRAFT", "ASSIGNED", "PENDING_HANDOVER"]

/** Status x action: false = the action does not belong to this status (render nothing). */
const APPLIES: Record<DeliveryAction, (s: DeliveryActionSubject) => boolean> = {
  detail: () => true,
  addToRoute: (s) => ROUTABLE.includes(s.deliveryNoteStatus ?? "") && LOCAL_CHANNEL(s.deliveryChannel),
  createRoute: (s) => ROUTABLE.includes(s.deliveryNoteStatus ?? "") && LOCAL_CHANNEL(s.deliveryChannel),
  notifyBuyer: (s) => !DN_TERMINAL.includes(s.deliveryNoteStatus ?? ""),
  consolidate: () => true,
  approveLoading: (s) => s.deliveryNoteStatus === "ASSIGNED", // approve.ts accepts ASSIGNED only
  trackLink: (s) => ACTIVE_DN_STATUSES.includes((s.deliveryNoteStatus ?? "") as (typeof ACTIVE_DN_STATUSES)[number]),
  callReceiver: () => true,
  reportFailed: (s) => ACTIVE_DN_STATUSES.includes((s.deliveryNoteStatus ?? "") as (typeof ACTIVE_DN_STATUSES)[number]),
  manualComplete: (s) => ["ASSIGNED", "LOADED", "DELIVERING"].includes(s.deliveryNoteStatus ?? ""),
  cancel: (s) => ["DRAFT", "ASSIGNED", "LOADED", "DELIVERING"].includes(s.deliveryNoteStatus ?? ""),
  regeneratePdf: () => true,
  // Reassigning the driver belongs to planning; once loaded the route owns the assignment.
  dnReassign: (s) => ["DRAFT", "ASSIGNED"].includes(s.deliveryNoteStatus ?? ""),
  addNote: () => true,
  // Proof subject: `status` = verificationStatus (a missing status counts as PENDING).
  podVerify: (s) => s.status == null || s.status === "PENDING" || s.status === "REQUIRES_REVIEW",
  podReject: (s) => s.status == null || s.status === "PENDING" || s.status === "REQUIRES_REVIEW",
  reassign: (s) => OPEN_EXCEPTION.includes(s.status ?? ""),
  confirmAttribution: (s) => OPEN_EXCEPTION.includes(s.status ?? ""),
  escalate: (s) => s.status === "OPEN" || s.status === "IN_PROGRESS",
  handoverCreate: () => true,
  handoverDetail: () => true,
  handoverInitiate: (s) => s.status === "DRAFT",
  senderVerify: (s) => s.status === "INITIATED",
  receiverVerify: (s) => s.status === "SENDER_VERIFIED",
  // BE has no SUBMITTED status: DRAFT -> SETTLED | DISPUTED | WRITTEN_OFF (contract §2 COD).
  codSettle: (s) => s.status === "DRAFT",
  codDispute: (s) => s.status === "DRAFT",
  codWriteOff: (s) => s.status === "DRAFT",
  pullSheetDetail: () => true,
  // A printable sheet exists once the loader has finished (CONFIRMED) or the keeper confirmed (LOADED).
  pullSheetPdf: (s) => ["CONFIRMED", "LOADED"].includes(s.pullSheetStatus ?? ""),
  pullSheetPdfRegenerate: (s) => ["CONFIRMED", "LOADED"].includes(s.pullSheetStatus ?? ""),
  shortageView: () => true,
  shortageReport: () => true,
  // Route: PLANNED -> LOADING -> IN_PROGRESS (start) -> COMPLETED; planning actions stop once it starts.
  routeStart: (s) => ROUTE_PLANNING.includes(s.status ?? ""),
  routeLoadingComplete: (s) => ROUTE_PLANNING.includes(s.status ?? ""),
  routeComplete: (s) => s.status === "IN_PROGRESS",
  routeReorder: (s) => ROUTE_PLANNING.includes(s.status ?? ""),
  routeOptimize: (s) => ROUTE_PLANNING.includes(s.status ?? ""),
  routeAssign: (s) => ROUTE_PLANNING.includes(s.status ?? ""),
  vehicleCreate: () => true,
  vehicleEdit: () => true,
  vehicleDelete: () => true,
}

/** Non-permission reasons (role, data) an applicable action is still unavailable. i18n key in `delivery`. */
const BLOCKED: Partial<Record<DeliveryAction, (s: DeliveryActionSubject, ctx: Ctx) => string | null>> = {
  senderVerify: (s, ctx) => (ctx.userId && s.fromUserId === ctx.userId ? null : "custody.detail.onlySenderCanVerify"),
  receiverVerify: (s, ctx) => (ctx.userId && s.toUserId === ctx.userId ? null : "custody.detail.onlyReceiverCanVerify"),
  callReceiver: (s) => (s.receiverPhone ? null : "manage.reason.noPhone"),
  // BE refuses start with error.route.not_loaded until every live DN is loaded and handed over.
  routeStart: (s) =>
    s.stops?.some((st) => DN_NOT_LOADED.includes(st.dn?.deliveryNoteStatus ?? "")) ? "error.route.not_loaded" : null,
  // contract §2: once the driver took the goods (dispatched_date) the BE answers 400 pull_sheet_dispatched.
  shortageReport: (s) => (s.dispatchedDate ? "error.shortage.pull_sheet_dispatched" : null),
  regeneratePdf: (s) =>
    s.allowedActions?.regeneratePdf
      ? s.allowedActions.regeneratePdf.allowed
        ? null
        : s.allowedActions.regeneratePdf.reason ?? "manage.reason.noReceipt"
      : s.hasReceipt === false
        ? "manage.reason.noReceipt"
        : null,
  // DN detail: the server's own precondition wins over the FE status set (O86).
  manualComplete: (s) => (s.allowedActions?.manualComplete && !s.allowedActions.manualComplete.allowed ? s.allowedActions.manualComplete.reason ?? "manage.reason.notInStatus" : null),
  cancel: (s) => (s.allowedActions?.cancel && !s.allowedActions.cancel.allowed ? s.allowedActions.cancel.reason ?? "manage.reason.notInStatus" : null),
}

export type DeliveryActionState = {
  /** The action belongs to this status at all; when false, render nothing. */
  applies: boolean
  /** The user may perform it now. */
  allowed: boolean
  /** Why it is unavailable (permission first, then role/data), null when allowed. */
  reason: string | null
}

export function deliveryActionApplies(action: DeliveryAction, subject: DeliveryActionSubject | null | undefined): boolean {
  if (!subject) return false
  return APPLIES[action](subject)
}

/**
 * `stateOf(action, subject)` for every delivery screen; `permissionState(action)` for buttons
 * without a row (page header, bulk bar). Permission is checked before the role/data reason.
 */
export function useDeliveryActions() {
  const { t } = useTranslation(["delivery", "common"])
  const userId = useSelector((s: { auth: IAuth }) => s.auth.user?.id ?? s.auth.userId ?? null)

  const granted: Partial<Record<IPermissionCode, boolean>> = {
    ORD_DN_DETAIL: usePermission("ORD_DN_DETAIL").hasAccess,
    DEL_ROUTE_PLAN: usePermission("DEL_ROUTE_PLAN").hasAccess,
    ORD_DN_MNG: usePermission("ORD_DN_MNG").hasAccess,
    ORD_DN_CREATE: usePermission("ORD_DN_CREATE").hasAccess,
    ORD_DN_PULLSHEET_ASSIGN: usePermission("ORD_DN_PULLSHEET_ASSIGN").hasAccess,
    ORD_DN_PDF: usePermission("ORD_DN_PDF").hasAccess,
    DEL_CHANNEL_OVERRIDE: usePermission("DEL_CHANNEL_OVERRIDE").hasAccess,
    BIZ_GOV_MNG: usePermission("BIZ_GOV_MNG").hasAccess,
    ORD_PS_DETAIL: usePermission("ORD_PS_DETAIL").hasAccess,
    ORD_PS_PDF: usePermission("ORD_PS_PDF").hasAccess,
    ORD_PS_MNG: usePermission("ORD_PS_MNG").hasAccess,
    BIZ_GOV_VIEW: usePermission("BIZ_GOV_VIEW").hasAccess,
  }

  const permissionState = useCallback(
    (action: DeliveryAction) => {
      const codes = DELIVERY_ACTION_PERMISSION[action]
      const allowed = codes.length === 0 || codes.some((c) => granted[c])
      return { allowed, reason: allowed ? null : t("common:permission.required", { code: codes.join(" / ") }) }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, JSON.stringify(granted)],
  )

  const stateOf = useCallback(
    (action: DeliveryAction, subject: DeliveryActionSubject | null | undefined): DeliveryActionState => {
      const applies = deliveryActionApplies(action, subject)
      const perm = permissionState(action)
      if (!perm.allowed) return { applies, ...perm }
      const blockKey = subject ? BLOCKED[action]?.(subject, { userId }) ?? null : null
      // BE precondition reasons are errors-namespace keys (`error.*`); the rest live in `delivery`.
      const reason = blockKey ? (blockKey.startsWith("error.") ? t(blockKey, { ns: "errors" }) : t(`delivery:${blockKey}`)) : null
      return { applies, allowed: !blockKey, reason }
    },
    [permissionState, t, userId],
  )

  /**
   * For screens that always show a fixed set of buttons (DN detail action bar): an action that does
   * not belong to the current status stays visible, disabled with «Энэ төлөвт боломжгүй».
   */
  const shownStateOf = useCallback(
    (action: DeliveryAction, subject: DeliveryActionSubject | null | undefined): DeliveryActionState => {
      const s = stateOf(action, subject)
      if (!s.allowed || s.applies) return s
      return { applies: false, allowed: false, reason: t("delivery:manage.reason.notInStatus") }
    },
    [stateOf, t],
  )

  return { stateOf, permissionState, shownStateOf }
}
